import { round } from "lodash";
import { IPoint } from "../interfaces/IPoint";
import { IRect } from "../interfaces/IRect";
import { UnitUtil } from "./UnitUtil";
import { ImageUtil } from "./ImageUtil";
import { EditorModel } from "../staticModels/EditorModel";
import { ViewPortActions } from "../logic/actions/ViewPortActions";

export class DrawUtil {
  public static clearCanvas(canvas: HTMLCanvasElement): void {
    const ctx: CanvasRenderingContext2D = canvas?.getContext("2d");
   
    if (!ctx) {
      console.error("Canvas context not found");
      return;
    }
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.filter = "none";
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.shadowColor = "rgba(0, 0, 0, 0)";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  /** True for near-white strokes that disappear on light image backgrounds */
  public static needsContrastOutline(color: string): boolean {
    if (!color) return false;
    const c = color.trim().toLowerCase();
    if (c === "#fff" || c === "#ffffff" || c === "white") return true;

    const hexMatch = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((ch) => ch + ch)
          .join("");
      }
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.85;
    }

    const rgbMatch = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (rgbMatch) {
      const r = Number(rgbMatch[1]);
      const g = Number(rgbMatch[2]);
      const b = Number(rgbMatch[3]);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.85;
    }

    return false;
  }

  private static strokePathWithContrast(
    ctx: CanvasRenderingContext2D,
    color: string,
    thickness: number,
    buildPath: () => void
  ): void {
    if (DrawUtil.needsContrastOutline(color)) {
      ctx.strokeStyle = "rgba(0, 0, 0, 0.59)";
      ctx.lineWidth = thickness;
      buildPath();
      ctx.stroke();
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
    buildPath();
    ctx.stroke();
  }

  public static drawLine(
    canvas: HTMLCanvasElement,
    startPoint: IPoint,
    endPoint: IPoint,
    color: string = "#111111",
    thickness: number = 1
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    ctx.lineCap = "round";
    DrawUtil.strokePathWithContrast(ctx, color, thickness, () => {
      ctx.beginPath();
      ctx.moveTo(startPoint.x, startPoint.y);
      ctx.lineTo(endPoint.x + 1, endPoint.y + 1);
    });
    ctx.restore();
  }

  public static drawRect(
    canvas: HTMLCanvasElement,
    LabelName: string,
    rect: IRect,
    color: string = "#fff",
    thickness: number = 1
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    DrawUtil.strokePathWithContrast(ctx, color, thickness, () => {
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.width, rect.height);
    });
    ctx.restore();
  }
  public static drawRectWithText(
    canvas: HTMLCanvasElement,
    LabelName: string,
    rect: IRect,
    color: string = "#fff",
    thickness: number = 1
  ): void {
    const scale =
      ImageUtil.getSize(EditorModel.image).width /
      ViewPortActions.calculateViewPortContentImageRect().width;
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    DrawUtil.strokePathWithContrast(ctx, color, thickness, () => {
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.width, rect.height);
    });
    ctx.restore();

    ctx.save();
    ctx.font = "bold 1rem Montserrat";
    ctx.fillStyle = "#fff";
    ctx.shadowColor = "#666666";
    ctx.shadowBlur = 7;
    var text =
      LabelName +
      " (W " +
      round(rect.width * scale) +
      ", H " +
      round(rect.height * scale) +
      ")";
    ctx.fillText(text, rect.x, rect.y - 8);
    ctx.restore();
  }

  public static drawRectWithFill(
    canvas: HTMLCanvasElement,
    rect: IRect,
    color: string = "#fff"
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.width, rect.height);
    ctx.fill();
    ctx.restore();

    // ctx.font = "18px Arial";
    // ctx.fillStyle = '#000';
    // ctx.fillRect(rect.x, rect.y-20, 50, 20);
    // ctx.fillStyle = '#fff';
    // ctx.fillText("123",rect.x,rect.y-5);
  }

  /** Draw static annotation rectangles with one canvas context state save. */
  public static drawRects(
    canvas: HTMLCanvasElement,
    rectangles: Array<{
      rect: IRect;
      lineColor: string;
      fillColor: string;
      thickness: number;
    }>
  ): void {
    if (rectangles.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.save();
    for (const { rect, lineColor, fillColor, thickness } of rectangles) {
      ctx.fillStyle = fillColor;
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

      if (DrawUtil.needsContrastOutline(lineColor)) {
        ctx.strokeStyle = "rgba(0, 0, 0, 0.59)";
        ctx.lineWidth = thickness;
        ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
      }
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = thickness;
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    }
    ctx.restore();
  }

  public static shadeEverythingButRect(
    canvas: HTMLCanvasElement,
    rect: IRect,
    color: string = "rgba(0, 0, 0, 0.7)"
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();
  }

  public static drawCircleWithFill(
    canvas: HTMLCanvasElement,
    anchorPoint: IPoint,
    radius: number = 2,
    color: string
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    const startAngleRad = UnitUtil.deg2rad(0);
    const endAngleRad = UnitUtil.deg2rad(360);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(
      anchorPoint.x,
      anchorPoint.y,
      radius,
      startAngleRad,
      endAngleRad,
      false
    );
    ctx.fill();
    ctx.restore();
  }

  public static drawCircle(
    canvas: HTMLCanvasElement,
    anchorPoint: IPoint,
    radius: number = 2,
    startAngleDeg: number,
    endAngleDeg: number,
    thickness: number = 20,
    color: string = "#ffffff"
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    const startAngleRad = UnitUtil.deg2rad(startAngleDeg);
    const endAngleRad = UnitUtil.deg2rad(endAngleDeg);
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
    ctx.beginPath();
    ctx.arc(
      anchorPoint.x,
      anchorPoint.y,
      radius,
      startAngleRad,
      endAngleRad,
      false
    );
    ctx.stroke();
    ctx.restore();
  }

  public static drawPolygon(
    canvas: HTMLCanvasElement,
    anchors: IPoint[],
    color: string = "#fff",
    thickness: number = 1
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    DrawUtil.strokePathWithContrast(ctx, color, thickness, () => {
      ctx.beginPath();
      ctx.moveTo(anchors[0]?.x, anchors[0]?.y);
      for (let i = 1; i < anchors.length; i++) {
        ctx.lineTo(anchors[i].x, anchors[i].y);
      }
      ctx.closePath();
    });
    ctx.restore();
  }

  public static drawPolygondoted(
    canvas: HTMLCanvasElement,
    anchors: IPoint[],
    color: string = "#fff",
    thickness: number = 4
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    const dashOffset = Math.floor(Date.now() / 20) % 20;
    ctx.setLineDash([6, 6]);
    ctx.lineDashOffset = -dashOffset;
    DrawUtil.strokePathWithContrast(ctx, color || "#fff", thickness || 5, () => {
      ctx.beginPath();
      ctx.moveTo(anchors[0]?.x, anchors[0]?.y);
      for (let i = 1; i < anchors.length; i++) {
        ctx.lineTo(anchors[i].x, anchors[i].y);
      }
      ctx.closePath();
    });
    ctx.restore();
  }
  public static drawPolylineWithFill(
    canvas: HTMLCanvasElement,
    anchors: IPoint[],
    color: string = "#fff"
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(anchors[0]?.x, anchors[0]?.y);
    for (let i = 1; i < anchors.length; i++) {
      ctx.lineTo(anchors[i].x, anchors[i].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  public static drawPolyline(
    canvas: HTMLCanvasElement,
    LabelName: string,
    anchors: IPoint[],
    color: string = "#fff",
    thickness: number = 1
  ): void {
    const scale =
      ImageUtil.getSize(EditorModel.image).width /
      ViewPortActions.calculateViewPortContentImageRect().width;
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    let c = 0;
    DrawUtil.strokePathWithContrast(ctx, color, thickness, () => {
      ctx.beginPath();
      ctx.moveTo(anchors[0]?.x, anchors[0]?.y);
      c = 0;
      for (let i = 1; i < anchors.length; i++) {
        ctx.lineTo(anchors[i].x, anchors[i].y);
        if (i < anchors.length) {
          c =
            c +
            Math.hypot(
              anchors[i - 1].x - anchors[i].x,
              anchors[i - 1].y - anchors[i].y
            );
        }
      }
    });
    ctx.restore();

    ctx.save();
    ctx.font = "bold 1rem Montserrat";
    ctx.fillStyle = color;
    ctx.shadowColor = "#666666";
    ctx.shadowBlur = 5;
    ctx.fillText(
      LabelName + "(L " + round(c * scale) + ")",
      anchors[0].x,
      anchors[0].y - 8
    );
    ctx.restore();
  }
  public static drawPolygonWithFill(
    canvas: HTMLCanvasElement,
    anchors: IPoint[],
    color: string = "#fff"
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(anchors[0]?.x, anchors[0]?.y);
    for (let i = 1; i < anchors.length; i++) {
      ctx.lineTo(anchors[i].x, anchors[i].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  public static drawText(
    canvas: HTMLCanvasElement,
    text: string,
    textSize: number,
    anchorPoint: IPoint,
    color: string = "#ffffff",
    bold: boolean = false,
    align: string = "center"
  ): void {
    const ctx: CanvasRenderingContext2D = canvas.getContext("2d");
    ctx.save();
    ctx.fillStyle = color;
    ctx.textAlign = align as CanvasTextAlign;
    ctx.textBaseline = "middle";
    ctx.font = (bold ? "bold " : "") + textSize + "px Arial";
    ctx.fillText(text, anchorPoint.x, anchorPoint.y);
    ctx.restore();
  }

  public static hexToRGB(hex: string, alpha: number | null = null): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha !== null) {
      return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
    } else {
      return "rgb(" + r + ", " + g + ", " + b + ")";
    }
  }
}
